import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetList } from "react-admin";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import FlashOnIcon from "@mui/icons-material/FlashOn";

import {
  formatMiqaatDate,
  formatMiqaatMealType,
  getMiqaatRelativeStatus,
  sortMiqaatsUpcomingFirst,
  findSoonestUpcomingMiqaat,
  matchMiqaatSearch,
  getValidationErrorMessage,
  type MiqaatRecord,
} from "./miqaat-selector-utils";

type MiqaatSelectorProps = {
  label?: string;
  helperText?: string;
};

export default function MiqaatSelector({
  label = "Select Upcoming Miqaat",
  helperText = "Search by event name, Hijri/English date, venue, or meal slot.",
}: MiqaatSelectorProps) {
  const { setValue, control, formState } = useFormContext();
  const miqaatId = useWatch({ control, name: "miqaatId" }) as string | undefined;

  const { data: miqaats = [], isLoading } = useGetList<MiqaatRecord>("miqaats", {
    pagination: { page: 1, perPage: 250 },
    sort: { field: "createdAt", order: "DESC" },
  });

  const sortedMiqaats = useMemo(() => sortMiqaatsUpcomingFirst(miqaats), [miqaats]);
  const soonestUpcoming = useMemo(() => findSoonestUpcomingMiqaat(miqaats), [miqaats]);

  const selectedMiqaat = useMemo(() => {
    if (!miqaatId?.trim()) return null;
    return miqaats.find((m) => String(m.id) === String(miqaatId)) ?? null;
  }, [miqaats, miqaatId]);

  const handleSelect = (id: string | null) => {
    setValue("miqaatId", id || null, {
      shouldDirty: true,
      shouldValidate: true,
    });
    // Reset recipients preview flag to prompt recipient reload
    setValue("recipientsPreviewed", false, { shouldDirty: true });
  };

  const hasError = Boolean(formState.errors.miqaatId);
  const errorMessage = getValidationErrorMessage(formState.errors.miqaatId);

  return (
    <Box sx={{ width: "100%", maxWidth: 640 }}>
      {/* Quick Select Suggestion for Next Upcoming Event */}
      {soonestUpcoming && (
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontSize: "0.7rem",
              lineHeight: 1,
            }}
          >
            Next Upcoming:
          </Typography>
          <Chip
            size="small"
            icon={<FlashOnIcon fontSize="small" color="warning" />}
            label={`${soonestUpcoming.name} · ${formatMiqaatDate(soonestUpcoming)}`}
            onClick={() => handleSelect(String(soonestUpcoming.id))}
            clickable
            color={selectedMiqaat?.id === soonestUpcoming.id ? "primary" : "default"}
            variant={selectedMiqaat?.id === soonestUpcoming.id ? "filled" : "outlined"}
          />
        </Box>
      )}

      {/* Autocomplete Input with Rich Dropdown Options */}
      <Autocomplete<MiqaatRecord, false, false, false>
        options={sortedMiqaats}
        value={selectedMiqaat}
        onChange={(_, newValue) => handleSelect(newValue ? String(newValue.id) : null)}
        getOptionLabel={(option) => String(option.name ?? "")}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
        filterOptions={(options, { inputValue }) =>
          options.filter((option) => matchMiqaatSearch(option, inputValue))
        }
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search by event name, Hijri/English date, venue…"
            error={hasError}
            helperText={hasError ? errorMessage : helperText}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const dateStr = formatMiqaatDate(option);
          const mealType = formatMiqaatMealType(option.mealType);
          const relativeStatus = getMiqaatRelativeStatus(option);
          const isLunch = option.mealType === "LUNCH";
          const isDinner = option.mealType === "DINNER";

          return (
            <Box component="li" {...props} key={String(option.id)}>
              <Box sx={{ width: "100%", py: 0.5 }}>
                {/* Row 1: Event Title and Badges */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.35,
                      flex: 1,
                      minWidth: 0,
                      wordBreak: "break-word",
                    }}
                  >
                    {String(option.name ?? "—")}
                  </Typography>

                  <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={isLunch ? "warning" : isDinner ? "info" : "default"}
                      label={mealType}
                    />

                    {relativeStatus && (
                      <Chip
                        size="small"
                        label={relativeStatus.label}
                        color={relativeStatus.color}
                        variant={
                          relativeStatus.isToday || relativeStatus.isUpcoming
                            ? "filled"
                            : "outlined"
                        }
                      />
                    )}
                  </Stack>
                </Box>

                {/* Row 2: Date & Venue */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: 0.5,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarMonthOutlinedIcon fontSize="inherit" color="inherit" />
                    <span>{dateStr}</span>
                  </Box>
                  {option.venue && (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      <LocationOnOutlinedIcon fontSize="inherit" color="inherit" />
                      <Typography variant="caption" color="inherit" noWrap sx={{ maxWidth: 240 }}>
                        {String(option.venue)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
}
